// Create a Service Hub / HR & Admin service request (generic engine).
import React, { useEffect, useState } from 'react';
import { wsAPI } from '../services/api';
import { Modal, Field, Input, Select, Textarea, FileDrop, FileChip, Spin, Note, uploadFiles, useProjects, today } from './ui';
import { notify } from '../utils/notify';

export const HUB_DEPARTMENTS = {
  'Engineering & Design': ['Drawing review', 'Technical query', 'Design calculation', 'Other'],
  'Procurement & Stores': ['Material request', 'Vendor support', 'Stock enquiry', 'Other'],
  'Project Execution': ['Site support', 'Mobilization', 'Subcontractor coordination', 'Other'],
  'Water Management Services': ['Maintenance', 'Lab support', 'Operator shift', 'Other'],
  'HR & Employee Services': ['Onboarding', 'Training', 'Employment letter', 'Other'],
  'Admin & Facilities': ['Vehicle', 'Visitor', 'Repairs', 'Stationery', 'Other'],
  'Finance & Accounts': ['Invoice', 'Payment status', 'Reimbursement query', 'Other'],
  'IT Service Desk': ['Access', 'Software', 'Incident', 'Hardware', 'Other'],
};

export const HR_SERVICES = {
  'HR letters': ['Employment letter', 'Experience certificate'],
  'Joining & exit': ['Onboarding', 'Clearance'],
  'Learning & training': ['Training nomination', 'Training completion'],
  'Transport & guest house': ['Vehicle booking', 'Stay request'],
  'Visitor & meeting support': ['Visitor pass', 'Meeting room'],
  'Office services': ['Stationery', 'Courier', 'Facility repair'],
};

// kind: 'service' (Service Hub) | 'hr_service' (HR & Admin)
export default function ServiceRequestModal({ open, onClose, onCreated, kind = 'service', department, service, businessUnit, location }) {
  const projects = useProjects();
  const [f, setF] = useState({});
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) {
      const dep = department || Object.keys(HUB_DEPARTMENTS)[0];
      setF({ department: dep, service: service || (kind === 'service' ? HUB_DEPARTMENTS[dep]?.[0] : 'Employment letter'), title: '', description: '', priority: 'Normal', requiredBy: '', project: '', location: location || '' });
      setFiles([]);
    }
  }, [open, department, service, kind, location]);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const hrServices = Object.values(HR_SERVICES).flat();

  const submit = async () => {
    if (!f.title?.trim()) { notify.error('Enter a short summary'); return; }
    if (!f.description?.trim()) { notify.error('Describe what you need'); return; }
    setSaving(true);
    try {
      const attachments = files.length ? await uploadFiles(files, 'workspace') : [];
      const r = await wsAPI.createRequest({
        type: kind, subType: f.service, title: f.title, description: f.description, priority: f.priority, requiredBy: f.requiredBy || undefined,
        project: f.project || undefined, businessUnit, location: f.location || undefined, attachments,
        data: kind === 'service' ? { department: f.department, service: f.service } : { service: f.service }, submit: true,
      });
      notify.success(`${r.request.refNo} created — assigned to the ${kind === 'service' ? f.department : 'HR / Admin'} desk`);
      onCreated && onCreated(r.request);
      onClose();
    } catch (e) { notify.error(e.message || 'Could not create'); } finally { setSaving(false); }
  };

  return (
    <Modal open={open} onClose={onClose} size="lg" title={kind === 'service' ? 'Create service request' : 'New HR & admin request'} sub="Routed to the right team with a target date and automatic escalation"
      footer={<><button type="button" className="tse-btn gray" onClick={onClose}>Cancel</button><button type="button" className="tse-btn" onClick={submit} disabled={saving}>{saving ? <Spin /> : <i className="bi bi-send" />}Submit request</button></>}>
      <div className="tse-form-grid c2">
        {kind === 'service' && <Field label="Department" required><Select value={f.department || ''} onChange={(e) => { set('department', e.target.value); set('service', HUB_DEPARTMENTS[e.target.value][0]); }} options={Object.keys(HUB_DEPARTMENTS)} /></Field>}
        <Field label="Service" required><Select value={f.service || ''} onChange={(e) => set('service', e.target.value)} options={kind === 'service' ? (HUB_DEPARTMENTS[f.department] || []) : hrServices} /></Field>
        <Field label="Summary" required className={kind === 'service' ? 'span-all' : ''}><Input value={f.title || ''} onChange={(e) => set('title', e.target.value)} placeholder="Short summary of the request" /></Field>
        <Field label="Details" required className="span-all"><Textarea rows={4} value={f.description || ''} onChange={(e) => set('description', e.target.value)} /></Field>
        <Field label="Priority"><Select value={f.priority || 'Normal'} onChange={(e) => set('priority', e.target.value)} options={['Low', 'Normal', 'High', 'Urgent']} /></Field>
        <Field label="Needed by"><Input type="date" min={today()} value={f.requiredBy || ''} onChange={(e) => set('requiredBy', e.target.value)} /></Field>
        <Field label="Project (optional)"><Select value={f.project || ''} onChange={(e) => set('project', e.target.value)} placeholder="—" options={projects.map((p) => p.projectCode)} /></Field>
        <Field label="Location"><Input value={f.location || ''} onChange={(e) => set('location', e.target.value)} placeholder="Office / site" /></Field>
        <Field label="Attachments" className="span-all">
          <FileDrop inline onFiles={(fs) => setFiles((p) => [...p, ...fs])} />
          {files.map((file, i) => <FileChip key={i} file={file} onRemove={() => setFiles((p) => p.filter((_, j) => j !== i))} />)}
        </Field>
      </div>
      <Note tone="plain" style={{ marginTop: 8 }}>You can track this request under My Requests; the desk owner is notified immediately.</Note>
    </Modal>
  );
}
